/**
 * CONSAI — Forgot Password (4 steps)
 * Design: matches forgotpassword_flow.html from Google Stitch
 */
import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [mobile, setMobile] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const otpRefs = useRef([]);

  const otp = otpDigits.join('');

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/[^0-9]/g, '').slice(-1);
    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      const newDigits = [...otpDigits];
      newDigits[index - 1] = '';
      setOtpDigits(newDigits);
      otpRefs.current[index - 1]?.focus();
    }
  };

  const startResendTimer = () => {
    setResendTimer(45);
    const interval = setInterval(() => {
      setResendTimer((prev) => { if (prev <= 1) { clearInterval(interval); return 0; } return prev - 1; });
    }, 1000);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    const cleanMobile = mobile.replace(/\D/g, '');
    if (cleanMobile.length !== 10) { toast.error(language === 'hi' ? 'कृपया 10 अंकों का मोबाइल नंबर दर्ज करें' : 'Please enter a valid 10-digit mobile number'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password/send-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile_number: cleanMobile })
      });
      const data = JSON.parse(await response.text());
      if (response.ok && data.success) {
        setResetToken(data.data?.reset_token || ''); setStep(2); startResendTimer();
        toast.success(language === 'hi' ? 'OTP भेजा गया' : 'OTP sent successfully');
      } else toast.error(data.detail || (language === 'hi' ? 'OTP भेजने में विफल' : 'Failed to send OTP'));
    } catch { toast.error(language === 'hi' ? 'कुछ गलत हो गया' : 'Something went wrong'); }
    finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error(language === 'hi' ? 'कृपया 6-अंकों का OTP दर्ज करें' : 'Please enter a valid 6-digit OTP'); return; }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile_number: mobile.replace(/\D/g, ''), otp, reset_token: resetToken })
      });
      const data = JSON.parse(await response.text());
      if (response.ok && data.success) { setStep(3); toast.success(language === 'hi' ? 'OTP सत्यापित' : 'OTP verified'); }
      else toast.error(data.detail || (language === 'hi' ? 'अमान्य OTP' : 'Invalid OTP'));
    } catch { toast.error(language === 'hi' ? 'सत्यापन विफल' : 'Verification failed'); }
    finally { setLoading(false); }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password/send-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile_number: mobile.replace(/\D/g, '') })
      });
      const data = JSON.parse(await response.text());
      if (response.ok && data.success) {
        setResetToken(data.data?.reset_token || ''); setOtpDigits(['', '', '', '', '', '']); startResendTimer();
        toast.success(language === 'hi' ? 'OTP पुनः भेजा गया' : 'OTP resent');
      } else toast.error(data.detail || (language === 'hi' ? 'विफल' : 'Failed to resend OTP'));
    } catch { toast.error(language === 'hi' ? 'कुछ गलत हो गया' : 'Something went wrong'); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error(language === 'hi' ? 'पासवर्ड कम से कम 8 अक्षरों का होना चाहिए' : 'Password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error(language === 'hi' ? 'पासवर्ड मेल नहीं खाते' : 'Passwords do not match'); return; }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      toast.error(language === 'hi' ? 'पासवर्ड में बड़ा, छोटा अक्षर और अंक होना चाहिए' : 'Password must contain uppercase, lowercase, and a number'); return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password/reset`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile_number: mobile.replace(/\D/g, ''), new_password: newPassword, reset_token: resetToken })
      });
      const data = JSON.parse(await response.text());
      if (response.ok && data.success) { setStep(4); toast.success(language === 'hi' ? 'पासवर्ड रीसेट सफल!' : 'Password reset successful!'); }
      else toast.error(data.detail || (language === 'hi' ? 'पासवर्ड रीसेट विफल' : 'Password reset failed'));
    } catch { toast.error(language === 'hi' ? 'कुछ गलत हो गया' : 'Something went wrong'); }
    finally { setLoading(false); }
  };

  const inputStyle = {
    width: '100%', background: '#0e0e0e', border: '1px solid #444748',
    borderRadius: '4px', padding: '12px 16px', color: '#ffffff',
    fontFamily: 'Manrope, sans-serif', fontSize: '16px', outline: 'none', boxSizing: 'border-box'
  };
  const primaryBtnStyle = {
    width: '100%', background: '#ffffff', color: '#2f3131', padding: '16px',
    borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '700',
    fontFamily: 'Manrope, sans-serif', fontSize: '16px', display: 'flex',
    alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'opacity 0.2s'
  };

  // Password strength checks
  const pwChecks = {
    length: newPassword.length >= 8,
    upper: /[A-Z]/.test(newPassword),
    special: /[^a-zA-Z0-9]/.test(newPassword),
  };

  return (
    <div
      data-testid="forgot-password-page"
      style={{ backgroundColor: '#0A0A0B', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: 'Manrope, sans-serif' }}
    >
      <div style={{ width: '100%', maxWidth: '400px', zIndex: 10 }}>

        {/* CONSAI Brand Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '6px' }}>
            <img src="/consai-logo.png" alt="CONSAI" style={{ height: '36px', width: 'auto' }} />
            <span className="horizon-font tracking-widest" style={{ fontSize: '36px', lineHeight: 1 }}>
              <span style={{ color: '#ffffff' }}>CONS</span><span style={{ color: '#d7263d' }}>AI</span>
            </span>
          </div>
          <p style={{ color: '#c4c7c8', fontFamily: 'Manrope, sans-serif', fontSize: '13px', letterSpacing: '0.05em' }}>
            {language === 'hi' ? 'एंटरप्राइज इंटेलिजेंस प्लेटफॉर्म' : 'Enterprise Intelligence Platform'}
          </p>
        </div>

        {/* Glass Card */}
        <div style={{ background: 'rgba(17,17,17,0.7)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid #262626', borderRadius: '12px', padding: '40px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

          {/* Step Indicators */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '40px' }}>
            {[1, 2, 3, 4].map((s) => (
              <div key={`fp-step-${s}`} style={{ width: '24px', height: '6px', borderRadius: '9999px', background: step === s ? '#ffffff' : step > s ? 'rgba(255,255,255,0.4)' : '#353534', transition: 'all 0.3s ease' }} />
            ))}
          </div>

          {/* STEP 1: Enter Mobile */}
          {step === 1 && (
            <div style={{ width: '100%' }} data-testid="forgot-step-1">
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '28px', fontWeight: '600', color: '#ffffff', marginBottom: '8px' }}>
                  {language === 'hi' ? 'पासवर्ड भूल गए?' : 'Forgot Password'}
                </h1>
                <p style={{ color: '#c4c7c8', fontSize: '14px' }}>
                  {language === 'hi' ? 'अपना पंजीकृत मोबाइल नंबर दर्ज करें' : 'Enter your registered mobile number to receive a verification code.'}
                </p>
              </div>
              <form onSubmit={handleSendOtp} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div>
                  <label style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#c4c7c8', display: 'block', marginBottom: '8px' }}>
                    {language === 'hi' ? 'मोबाइल नंबर' : 'Mobile Number'}
                  </label>
                  <div style={{ display: 'flex' }}>
                    <div style={{ background: '#0e0e0e', border: '1px solid #444748', borderRadius: '4px 0 0 4px', padding: '12px 16px', color: '#c4c7c8', fontFamily: 'Manrope, sans-serif', fontSize: '16px', whiteSpace: 'nowrap' }}>+91</div>
                    <input type="tel" value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                      placeholder={language === 'hi' ? '10 अंक नंबर' : '10-digit number'}
                      style={{ ...inputStyle, borderRadius: '0 4px 4px 0', borderLeft: 'none' }} data-testid="forgot-mobile-input"
                      onFocus={(e) => e.target.style.borderColor = '#ffffff'}
                      onBlur={(e) => e.target.style.borderColor = '#444748'} />
                  </div>
                </div>
                <button type="submit" disabled={loading} style={{ ...primaryBtnStyle, opacity: loading ? 0.8 : 1 }} data-testid="send-otp-btn">
                  {loading ? <><Loader2 size={18} className="animate-spin" /> {language === 'hi' ? 'भेजा जा रहा है...' : 'Sending...'}</> : (language === 'hi' ? 'OTP भेजें' : 'Send OTP')}
                </button>
                <div style={{ textAlign: 'center' }}>
                  <Link to="/login" style={{ color: '#c4c7c8', fontSize: '14px', textDecoration: 'none' }} className="hover:text-white" data-testid="back-to-login-link">
                    {language === 'hi' ? '← लॉगिन पर वापस' : '← Back to Login'}
                  </Link>
                </div>
              </form>
            </div>
          )}

          {/* STEP 2: OTP */}
          {step === 2 && (
            <div style={{ width: '100%' }} data-testid="forgot-step-2">
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <div style={{ width: '64px', height: '64px', background: '#2a2a2a', border: '1px solid #444748', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                  <span className="material-symbols-outlined" style={{ color: '#ffffff', fontSize: '28px' }}>sms</span>
                </div>
                <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '28px', fontWeight: '600', color: '#ffffff', marginBottom: '8px' }}>
                  {language === 'hi' ? 'OTP सत्यापित करें' : 'Verify Identity'}
                </h1>
                <p style={{ color: '#c4c7c8', fontSize: '14px' }}>
                  {language === 'hi' ? `+91 ${mobile} पर OTP भेजा` : `Code sent to +91 ${mobile}`}
                </p>
              </div>
              <form onSubmit={handleVerifyOtp} style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  {otpDigits.map((digit, index) => (
                    <input
                      key={`fp-otp-${index}`}
                      ref={(el) => { otpRefs.current[index] = el; }}
                      type="number"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      data-testid={`forgot-otp-input-${index}`}
                      style={{ width: '48px', height: '56px', background: '#0e0e0e', border: '1px solid #444748', borderRadius: '4px', textAlign: 'center', color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '24px', fontWeight: '600', outline: 'none', MozAppearance: 'textfield' }}
                      onFocus={(e) => e.target.style.borderColor = '#ffffff'}
                      onBlur={(e) => e.target.style.borderColor = '#444748'}
                    />
                  ))}
                </div>
                <button type="submit" disabled={loading} style={{ ...primaryBtnStyle, opacity: loading ? 0.8 : 1 }} data-testid="verify-otp-btn">
                  {loading ? <><Loader2 size={18} className="animate-spin" /> {language === 'hi' ? 'सत्यापित हो रहा है...' : 'Verifying...'}</> : (language === 'hi' ? 'OTP सत्यापित करें' : 'Verify Code')}
                </button>
                <div style={{ textAlign: 'center', fontFamily: 'Manrope, sans-serif', fontSize: '14px', color: '#c4c7c8' }}>
                  {language === 'hi' ? 'OTP नहीं मिला? ' : "Didn't receive code? "}
                  <button type="button" onClick={handleResendOtp} disabled={resendTimer > 0 || loading}
                    style={{ color: resendTimer > 0 ? 'rgba(196,199,200,0.4)' : '#ffffff', fontWeight: '700', background: 'none', border: 'none', cursor: resendTimer > 0 ? 'not-allowed' : 'pointer', fontFamily: 'Manrope, sans-serif', fontSize: '14px' }}>
                    {resendTimer > 0 ? `${language === 'hi' ? 'पुनः भेजें' : 'Resend'} (${resendTimer}s)` : (language === 'hi' ? 'पुनः भेजें' : 'Resend')}
                  </button>
                </div>
                <button type="button" onClick={() => { setStep(1); setOtpDigits(['', '', '', '', '', '']); }}
                  style={{ width: '100%', background: 'none', border: 'none', color: '#c4c7c8', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', fontSize: '14px', textAlign: 'center' }}>
                  ← {language === 'hi' ? 'नंबर बदलें' : 'Change Number'}
                </button>
              </form>
            </div>
          )}

          {/* STEP 3: Set New Password */}
          {step === 3 && (
            <div style={{ width: '100%' }} data-testid="forgot-step-3">
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <div style={{ width: '64px', height: '64px', background: 'rgba(98,255,150,0.1)', border: '1px solid rgba(98,255,150,0.3)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                  <span className="material-symbols-outlined" style={{ color: '#00e475', fontSize: '28px' }}>vpn_key</span>
                </div>
                <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '28px', fontWeight: '600', color: '#ffffff', marginBottom: '8px' }}>
                  {language === 'hi' ? 'नया पासवर्ड' : 'New Password'}
                </h1>
                <p style={{ color: '#c4c7c8', fontSize: '14px' }}>
                  {language === 'hi' ? 'अपने खाते की सुरक्षा के लिए एक मजबूत पासवर्ड बनाएं' : 'Create a strong, unique password for your account security.'}
                </p>
              </div>
              <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#c4c7c8', display: 'block', marginBottom: '8px' }}>
                    {language === 'hi' ? 'नया पासवर्ड' : 'New Password'}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="••••••••" style={{ ...inputStyle, paddingRight: '44px' }} data-testid="new-password-input"
                      onFocus={(e) => e.target.style.borderColor = '#ffffff'}
                      onBlur={(e) => e.target.style.borderColor = '#444748'} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#c4c7c8' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>{showPassword ? 'visibility_off' : 'visibility'}</span>
                    </button>
                  </div>
                </div>
                <div>
                  <label style={{ fontFamily: 'Manrope, sans-serif', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#c4c7c8', display: 'block', marginBottom: '8px' }}>
                    {language === 'hi' ? 'पासवर्ड की पुष्टि करें' : 'Confirm Password'}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••" style={inputStyle} data-testid="confirm-password-input"
                      onFocus={(e) => e.target.style.borderColor = '#ffffff'}
                      onBlur={(e) => e.target.style.borderColor = '#444748'} />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#c4c7c8' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>{showConfirmPassword ? 'visibility_off' : 'visibility'}</span>
                    </button>
                  </div>
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p style={{ marginTop: '6px', fontSize: '12px', color: '#f87171' }}>
                      {language === 'hi' ? 'पासवर्ड मेल नहीं खाते' : 'Passwords do not match'}
                    </p>
                  )}
                </div>

                {/* Password requirements */}
                <div style={{ background: '#1c1b1b', padding: '16px', borderRadius: '8px', border: '1px solid #444748', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[
                    { check: pwChecks.length, label: language === 'hi' ? 'कम से कम 8 अक्षर' : 'At least 8 characters' },
                    { check: pwChecks.upper, label: language === 'hi' ? 'एक बड़ा अक्षर' : 'One uppercase letter' },
                    { check: pwChecks.special, label: language === 'hi' ? 'एक विशेष अक्षर' : 'One special character' },
                  ].map(({ check, label }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: check ? '#00e475' : '#c4c7c8' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '16px', fontVariationSettings: check ? "'FILL' 1" : "'FILL' 0" }}>
                        {check ? 'check_circle' : 'circle'}
                      </span>
                      <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: '14px' }}>{label}</span>
                    </div>
                  ))}
                </div>

                <button type="submit" disabled={loading} style={{ ...primaryBtnStyle, opacity: loading ? 0.8 : 1 }} data-testid="reset-password-btn">
                  {loading ? <><Loader2 size={18} className="animate-spin" /> {language === 'hi' ? 'रीसेट हो रहा है...' : 'Resetting...'}</> : (language === 'hi' ? 'पासवर्ड रीसेट करें' : 'Reset Password')}
                </button>
              </form>
            </div>
          )}

          {/* STEP 4: Success */}
          {step === 4 && (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }} data-testid="forgot-step-4">
              <div style={{ width: '96px', height: '96px', background: 'rgba(98,255,150,0.2)', border: '1px solid rgba(98,255,150,0.5)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '32px' }}>
                <span className="material-symbols-outlined" style={{ color: '#00e475', fontSize: '56px', fontVariationSettings: "'FILL' 1" }}>check_circle</span>
              </div>
              <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: '28px', fontWeight: '600', color: '#ffffff', marginBottom: '8px' }}>
                {language === 'hi' ? 'पासवर्ड रीसेट सफल!' : 'Password Reset'}
              </h1>
              <p style={{ color: '#c4c7c8', fontSize: '14px', marginBottom: '40px', maxWidth: '280px' }}>
                {language === 'hi' ? 'आपका पासवर्ड सफलतापूर्वक अपडेट हो गया है। अब आप नए क्रेडेंशियल से लॉगिन कर सकते हैं।' : 'Your password has been successfully updated. You can now login with your new credentials.'}
              </p>
              <button onClick={() => navigate('/login')} style={{ ...primaryBtnStyle, boxShadow: '0 0 20px rgba(255,255,255,0.15)' }} data-testid="go-to-login-btn">
                {language === 'hi' ? 'लॉगिन पर जाएं' : 'Go to Login'}
              </button>
            </div>
          )}
        </div>

        {/* Brand Footer */}
        <div style={{ marginTop: '32px', textAlign: 'center' }}>
          <span className="horizon-font tracking-widest" style={{ color: '#8e9192', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
            CONSAI Enterprise Security
          </span>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
